import { Table, TableSorting } from "@/components/elements/Table";
import { LocalTableSorting } from "@/components/elements/Table/LocalTableSorting";
import { useTable } from "@/hooks/useTable";
import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { FCC } from "@/types/common";
import { Stack, Typography, IconButton, Menu, MenuItem, Box } from "@mui/material";
import { EMPTY_STR } from "@/constants/string";
import { CASE_ID_NOT_FOUND_MESSAGE, NO_TALENT_MESSAGE } from "@/constants/common";
import { findLabelByValue } from "@/utils/object";
import { useEnum } from "@/api/services/global/enum";
import { MRT_ColumnDef } from "material-react-table";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { IconNavigation } from "@/assets/icons";
import { useVisible } from "@/hooks/useVisible";
import { button, divider, state } from "@/theme/colors";
import { TalentInfoModal } from "@/features/case/components/CaseEdit/TalentInfoModal";
import { CaseMatchInfoModal } from "@/features/case/components/CaseEdit/CaseMatchInfoModal";
import { CaseMatchDetailModal } from "@/features/case/components/CaseEdit/CaseMatchDetailModal";
import { CASE_MATCH_STATUS_COLOR, ECaseMatchStatus, CaseMatchStatusOptions, EXPIRY_STATUS, CASE_MATCH_TERMINAL_STATUSES } from "@/types/enum";
import { SelectColor } from "@/components/elements/SelectColor";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { ConfirmModal } from "@/components/commons/ConfirmModal";
import { useDialog } from "@/hooks/useDialog";
import { truncateWithEllipsis } from "@/utils/string";
import { TalentAvatarCell } from "@/components/commons/TalentAvatarCell";
import { SelectTalentStatusModal } from "@/features/case/components/CaseEdit/SelectTalentStatusModal";
import { BulkChangeTalentStatusModal, TBulkStatusChange } from "@/features/case/components/CaseEdit/BulkChangeTalentStatusModal";
import { checkMissingRequiredFields } from "@/features/case/utils/checkMissingRequiredFields";
import { isExemptSpecifiedSkillCert } from "@/features/case/utils/is-exempt-specified-skill-cert";
import {
  hasClassificationMismatch,
  getExpiryStatus,
  TExpiryStatus,
  getAffectedDateFieldsOnDowngrade,
  getEmptyOptionalFieldLabels,
  getDateFieldKeysToClear,
} from "@/features/case/utils/caseMatchHelper";
import { downloadSingleResumeApi } from "@/api/services/main/talent";
import { downloadSharedResumeApi, downloadSharedAllResumesApi, exportSharedCaseMatchTalentsAsCsvApi } from "@/api/services/main/sharing";
import {
  downloadAllResumesByCaseApi,
  exportCaseMatchTalentsAsCsvApi,
  exportCaseMatchDocxZipApi,
  exportCaseMatchDocxZipForMatchApi,
} from "@/api/services/main/case";
import { saveAs } from "file-saver";
import { TGetCaseMatchesReqDto } from "@/api/services/main/case/dtos/get-case-matches.dto";
import { showError } from "@/utils/error";
import { downloadBlobResponse } from "@/utils/export";
import { LoadingScreen } from "@/components/elements/Loading/Loading";
import { toast } from "@/utils/toast";
import { useCaseScheduleList } from "@/api/services/main/case-schedule";
import { dayjs } from "@/utils/dayjs";
import { SharedCaseMatchDetailModal } from "@/features/sharing/components/SharedCaseMatchDetailModal";
import { SharedTalentInfoModal } from "@/features/sharing/components/SharedTalentInfoModal";
import {
  CLASSIFICATION_MISMATCH_MESSAGE,
  PASSPORT_EXPIRED_MESSAGE,
  PASSPORT_EXPIRING_SOON_MESSAGE,
  RESIDENCE_EXPIRED_MESSAGE,
  RESIDENCE_EXPIRING_SOON_MESSAGE,
  WARNING_CHANGE_STATUS_MESSAGE,
} from "@/features/case/utils/messageWarning";
import { TCaseEditMatchDisplayColumnSetting } from "@/features/case/components/CaseEdit/case-edit-match-filter-shared";
import { DATE_FORMAT } from "@/constants/datetime";
import { CaseMatchTagList } from "@/features/case/components/CaseMatchTagList";

type TCaseMatchTableProps = {
  rows: TCaseMatches[];
  totalCount?: number;
  onStatusMapChange?: (statusMap: Record<number, number>) => void;
  onTalentStatusMapChange?: (talentStatusMap: Record<number, number>) => void;
  onDeleteCaseMatch?: (caseMatchId: number) => Promise<void>;
  onCaseMatchMissingFieldsUpdate?: (updates: Record<number, boolean>) => void;
  onAutoSaveCaseMatchStatus?: (caseMatchId: number, newStatus?: number, newTalentStatus?: number, previousStatus?: number) => Promise<void>;
  canEdit?: boolean;
  caseId?: number | string;
  caseName?: string;
  jobClassificationIds?: (string | number)[];
  jobResidenceStatusIds?: number[];
  isSharedView?: boolean;
  shareUrl?: string;
  currentSort?: string;
  onSort?: (sortKey: string) => void;
  displayColumns?: TCaseEditMatchDisplayColumnSetting[];
  filterParams?: Partial<TGetCaseMatchesReqDto>;
};

type BulkActionsHeaderProps = {
  caseId?: number | string;
  totalCount?: number;
  caseName?: string;
  shareUrl?: string;
  isSharedView?: boolean;
  onBulkChangeTalentStatus?: () => void;
  filterParams?: Partial<TGetCaseMatchesReqDto>;
  canExportDocx?: boolean;
};

const BulkActionsHeader: React.FC<BulkActionsHeaderProps> = ({
  caseId,
  totalCount,
  caseName,
  shareUrl,
  isSharedView = false,
  onBulkChangeTalentStatus,
  filterParams,
  canExportDocx = false,
}) => {
  const menuAnchorRef = useRef<HTMLButtonElement>(null);
  const menuVisible = useVisible(false);
  const [isExportingDocx, setIsExportingDocx] = useState(false);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  const handleExportDocx = async () => {
    menuVisible.close();
    if (isExportingDocx) return;
    if (!caseId) {
      toast.error(CASE_ID_NOT_FOUND_MESSAGE);
      return;
    }
    setIsExportingDocx(true);

    try {
      const { blob, fileName } = await exportCaseMatchDocxZipApi(caseId, ["employment-notice", "offer-acceptance"]);
      saveAs(blob, fileName);
    } catch {
      toast.error("ファイルの出力に失敗しました。");
    } finally {
      setIsExportingDocx(false);
    }
  };

  const handleExportCsv = async () => {
    menuVisible.close();
    if (!shareUrl && !caseId) {
      toast.error(CASE_ID_NOT_FOUND_MESSAGE);
      return;
    }
    if (!totalCount || totalCount === 0) {
      toast.error(NO_TALENT_MESSAGE);
      return;
    }

    setIsBulkDownloading(true);
    try {
      let res;
      if (shareUrl) {
        res = await exportSharedCaseMatchTalentsAsCsvApi(shareUrl);
      } else if (caseId) {
        res = await exportCaseMatchTalentsAsCsvApi(caseId, filterParams);
      }
      if (res) {
        downloadBlobResponse(res, `${caseName || "案件"}_${dayjs().format("YYYYMMDD")}_${totalCount}名.csv`);
      }
    } catch (error) {
      showError(error);
    } finally {
      setIsBulkDownloading(false);
    }
  };

  const handleBulkDownloadResume = async () => {
    menuVisible.close();
    if (!shareUrl && !caseId) {
      toast.error(CASE_ID_NOT_FOUND_MESSAGE);
      return;
    }
    if (!totalCount || totalCount === 0) {
      toast.error(NO_TALENT_MESSAGE);
      return;
    }

    setIsBulkDownloading(true);
    try {
      const response = shareUrl ? await downloadSharedAllResumesApi(shareUrl) : await downloadAllResumesByCaseApi(caseId!);
      const blob = new Blob([response.data], {
        type: "application/zip",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${caseName || "履歴書"}_人材の履歴書.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError(error);
    } finally {
      setIsBulkDownloading(false);
    }
  };

  return (
    <>
      {(isBulkDownloading || isExportingDocx) && <LoadingScreen />}
      <IconButton ref={menuAnchorRef} onClick={menuVisible.open} size="small">
        <IconNavigation />
      </IconButton>
      <Menu
        anchorEl={menuAnchorRef.current}
        open={menuVisible.visible}
        onClose={menuVisible.close}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleExportCsv} disabled={!totalCount}>
          CSVでリストを一括ダウンロード
        </MenuItem>
        <MenuItem onClick={handleBulkDownloadResume}>履歴書を一括ダウンロード</MenuItem>
        {!shareUrl && (
          <MenuItem onClick={handleExportDocx} disabled={!canExportDocx || isExportingDocx}>
            採用通知表・内定承諾書の作成
          </MenuItem>
        )}
        {!isSharedView && (
          <Box sx={{ width: "100%", borderTop: `1px solid ${divider.high}` }}></Box>
        )}
        {!isSharedView && (
          <MenuItem
            onClick={() => {
              menuVisible.close();
              onBulkChangeTalentStatus?.();
            }}
          >
            人材ステータスを一括変更
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

type ActionMenuCellProps = {
  row: TCaseMatches;
  caseId?: number | string;
  onViewTalent: (talentId: number) => void;
  onViewCaseMatch: (caseMatch: TCaseMatches) => void;
  onDelete: (caseMatch: TCaseMatches) => void;
  hasMissingFields?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canExportDocx?: boolean;
  isSharedView?: boolean;
  shareUrl?: string;
};

const ActionMenuCell: React.FC<ActionMenuCellProps> = ({
  row,
  caseId,
  onViewTalent,
  onViewCaseMatch,
  onDelete,
  hasMissingFields = false,
  canEdit = true,
  canDelete = true,
  canExportDocx = false,
  isSharedView = false,
  shareUrl,
}) => {
  const menuAnchorRef = useRef<HTMLButtonElement>(null);
  const menuVisible = useVisible(false);

  const exportDocxZip = async () => {
    if (!caseId) {
      toast.error(CASE_ID_NOT_FOUND_MESSAGE);
      return;
    }
    try {
      const { blob, fileName } = await exportCaseMatchDocxZipForMatchApi(caseId, row.id, row.talent?.fullName ?? row.talent?.nickName);
      saveAs(blob, fileName);
    } catch {
      toast.error("ファイルの出力に失敗しました。");
    }
  };

  const handleMenuItemClick = async (action: string) => {
    menuVisible.close();
    if (action === "viewTalent" && row.talent?.id) {
      onViewTalent(row.talent.id);
    } else if (action === "viewCaseMatch") {
      onViewCaseMatch(row);
    } else if (action === "delete") {
      onDelete(row);
    } else if (action === "exportOfferDocs") {
      await exportDocxZip();
    } else if (action === "downloadResume" && row.talent?.id) {
      try {
        const response =
          isSharedView && shareUrl ? await downloadSharedResumeApi(row.talent.id, shareUrl) : await downloadSingleResumeApi(row.talent.id);
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const displayName =
          isSharedView && row.talent?.fullName
            ? row.talent.fullName
                .trim()
                .split(/\s+/)
                .map((p: string) => p.charAt(0).toUpperCase())
                .join(".")
            : (row.talent?.fullName ?? row.talent?.nickName);
        link.download = `${displayName}の履歴書.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        showError(error);
      }
    }
  };

  return (
    <>
      <IconButton ref={menuAnchorRef} onClick={menuVisible.open} size="small">
        <IconNavigation />
      </IconButton>
      <Menu
        anchorEl={menuAnchorRef.current}
        open={menuVisible.visible}
        onClose={menuVisible.close}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          ".MuiPaper-root": {
            padding: 0,
          },
          ".MuiMenu-list": {
            padding: 0,
          },
        }}
      >
        <MenuItem onClick={() => handleMenuItemClick("viewTalent")}>人材情報を確認</MenuItem>
        <MenuItem
          onClick={() => handleMenuItemClick("viewCaseMatch")}
          sx={
            hasMissingFields && !isSharedView
              ? {
                  color: `${state.warning_1} !important`,
                  "&:hover": {
                    color: `${state.warning_1} !important`,
                    backgroundColor: "rgba(236, 113, 38, 0.08) !important",
                  },
                  "&:focus": {
                    color: `${state.warning_1} !important`,
                  },
                  "&.Mui-focused": {
                    color: `${state.warning_1} !important`,
                  },
                }
              : {}
          }
        >
          マッチング情報を確認
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("downloadResume")}>履歴書をダウンロード</MenuItem>
        {canExportDocx && !isSharedView && <MenuItem onClick={() => handleMenuItemClick("exportOfferDocs")}>採用通知表・内定承諾書の作成</MenuItem>}
        {canDelete && !isSharedView && (
          <>
            <Box sx={{ width: "100%", borderTop: `1px solid ${divider.high}` }}></Box>
            <MenuItem
              sx={{
                color: `${button.danger} !important`,
                "&:hover": {
                  color: `${button.danger} !important`,
                },
                "&:focus": {
                  color: `${button.danger} !important`,
                },
                "&.Mui-focused": {
                  color: `${button.danger} !important`,
                },
              }}
              onClick={() => handleMenuItemClick("delete")}
              disabled={!canEdit}
            >
              マッチング一覧から削除
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

export const CaseEditMatchTable: FCC<TCaseMatchTableProps> = ({
  rows,
  totalCount,
  onStatusMapChange,
  onTalentStatusMapChange,
  onDeleteCaseMatch,
  onCaseMatchMissingFieldsUpdate,
  onAutoSaveCaseMatchStatus,
  canEdit = true,
  caseId,
  caseName,
  jobClassificationIds = [],
  jobResidenceStatusIds = [],
  isSharedView = false,
  shareUrl,
  currentSort,
  onSort,
  displayColumns: displayColumnSettings,
  filterParams,
}) => {
  const MAX_SHOW_TEXT_LENGTH = 15;
  const getExemptSpecifiedSkillCert = useCallback(
    (talentResidenceStatusLabel?: string): boolean => isExemptSpecifiedSkillCert(jobResidenceStatusIds, talentResidenceStatusLabel),
    [jobResidenceStatusIds],
  );
  const [selectedTalentId, setSelectedTalentId] = React.useState<number | undefined>();
  const [selectedCaseMatch, setSelectedCaseMatch] = React.useState<TCaseMatches | undefined>();
  const [caseMatchToDelete, setCaseMatchToDelete] = React.useState<TCaseMatches | null>(null);
  const [pendingCaseMatchId, setPendingCaseMatchId] = React.useState<number | null>(null);
  const [pendingPreviousStatus, setPendingPreviousStatus] = React.useState<number | null>(null);
  const [pendingDowngradeInfo, setPendingDowngradeInfo] = React.useState<{
    currentStatus: number;
    newStatus: number;
    caseMatch?: TCaseMatches;
  } | null>(null);
  const [emptyOptionalFields, setEmptyOptionalFields] = React.useState<string[]>([]);
  const confirmOptionalFieldsDialog = useDialog();
  const talentInfoModalVisible = useVisible(false);
  const CaseMatchInfoModalVisible = useVisible(false);
  const CaseMatchDetailModalVisible = useVisible(false);
  const confirmDeleteDialog = useDialog();
  // Second-step confirm shown only when the talent being deleted is the LAST match in the case.
  // BE (DEV-292) soft-deletes the entire case once its last match is removed — warn the user first.
  const confirmDeleteLastMatchDialog = useDialog();
  const confirmStatusDowngradeDialog = useDialog();
  const selectTalentStatusModalVisible = useVisible(false);
  const bulkChangeTalentStatusModalVisible = useVisible(false);
  const [talentStatusMap, setTalentStatusMap] = useState<Record<number, number>>({});
  const [localClearedFields, setLocalClearedFields] = useState<Record<number, string[]>>({});
  const { data: caseSchedulesData } = useCaseScheduleList(caseId);
  const [statusMap, setStatusMap] = useState<Record<number, number>>(() => {
    const initial: Record<number, number> = {};
    rows.forEach((row) => {
      if (row.id) {
        initial[row.id] = row.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
      }
    });
    return initial;
  });

  useEffect(() => {
    setStatusMap((prev) => {
      const updated = { ...prev };
      rows.forEach((row) => {
        if (row.id) {
          // In shared view (read-only), always sync with server data
          // In internal view, only add new entries to preserve local edits
          if (isSharedView || !(row.id in updated)) {
            updated[row.id] = row.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
          }
        }
      });
      return updated;
    });
  }, [rows, isSharedView]);

  useEffect(() => {
    if (onStatusMapChange) {
      onStatusMapChange(statusMap);
    }
  }, [statusMap, onStatusMapChange]);

  useEffect(() => {
    if (onTalentStatusMapChange) {
      onTalentStatusMapChange(talentStatusMap);
    }
  }, [talentStatusMap, onTalentStatusMapChange]);

  const handleViewTalent = (talentId: number) => {
    setSelectedTalentId(talentId);
    talentInfoModalVisible.open();
  };

  const handleViewCaseMatch = (caseMatch: TCaseMatches) => {
    const savedStatus = caseMatch.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
    const currentStatus = caseMatch.id ? (statusMap[caseMatch.id] ?? savedStatus) : savedStatus;

    // If downgrade happened, clear affected date fields locally before opening modal
    // This handles the case where API cleared the fields but refetch hasn't completed yet
    let caseMatchToSet = caseMatch;
    const clearedFields = caseMatch.id ? localClearedFields[caseMatch.id] : undefined;
    if (clearedFields || currentStatus < savedStatus) {
      const fields = clearedFields || getDateFieldKeysToClear(savedStatus, currentStatus);
      if (fields.length > 0) {
        caseMatchToSet = { ...caseMatch };
        fields.forEach((field) => {
          (caseMatchToSet as Record<string, unknown>)[field] = null;
        });
      }
    }
    setSelectedCaseMatch(caseMatchToSet);

    if (isSharedView) {
      CaseMatchDetailModalVisible.open();
      return;
    }

    const isExempt = getExemptSpecifiedSkillCert(caseMatchToSet.talent?.residenceStatus?.residenceStatus);
    const hasMissingFields = checkMissingRequiredFields(caseMatchToSet, currentStatus, isExempt);

    if (hasMissingFields) {
      CaseMatchInfoModalVisible.open();
    } else {
      CaseMatchDetailModalVisible.open();
    }
  };

  const handleCloseTalentModal = () => {
    talentInfoModalVisible.close();
    setSelectedTalentId(undefined);
  };

  const handleCloseCaseMatchModal = () => {
    CaseMatchInfoModalVisible.close();
    setSelectedCaseMatch(undefined);
  };

  const handleCloseCaseMatchDetailModal = () => {
    CaseMatchDetailModalVisible.close();
    setSelectedCaseMatch(undefined);
  };

  const handleEditFromDetail = () => {
    CaseMatchDetailModalVisible.close();
    if (selectedCaseMatch) {
      CaseMatchInfoModalVisible.open();
    }
  };

  useEffect(() => {
    if (selectedCaseMatch?.id) {
      const updatedCaseMatch = rows.find((row) => row.id === selectedCaseMatch.id);
      if (updatedCaseMatch && updatedCaseMatch !== selectedCaseMatch) {
        setSelectedCaseMatch(updatedCaseMatch);
      }
    }
  }, [rows, selectedCaseMatch?.id]);

  const handleCaseMatchSaveSuccess = useCallback(async () => {
    CaseMatchInfoModalVisible.close();

    // Clear local cleared fields tracking for this case match
    if (selectedCaseMatch?.id) {
      setLocalClearedFields((prev) => {
        const updated = { ...prev };
        delete updated[selectedCaseMatch.id];
        return updated;
      });
    }

    // After saving case match info, check if we should auto-save the status
    // The status will be auto-saved if the missing fields warning was showing and now it's resolved
    if (selectedCaseMatch?.id && onAutoSaveCaseMatchStatus) {
      const caseMatchId = selectedCaseMatch.id;
      const savedStatus = selectedCaseMatch.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
      const currentStatus = statusMap[caseMatchId] ?? savedStatus;
      const hasStatusChanged = currentStatus !== savedStatus;

      // If status was changed (which caused the warning), auto-save the status
      if (hasStatusChanged) {
        // Small delay to allow the refetch to complete and check missing fields
        setTimeout(async () => {
          await onAutoSaveCaseMatchStatus(caseMatchId);
        }, 500);
      }
    }
  }, [selectedCaseMatch, statusMap, onAutoSaveCaseMatchStatus, setLocalClearedFields]);

  // Check if a schedule is expired (passed)
  const isScheduleExpired = useCallback((schedule: { dateConfirm?: string | null; startTimeConfirm?: string | null }): boolean => {
    if (!schedule.dateConfirm || !schedule.startTimeConfirm) {
      return false;
    }
    const startDateTime = dayjs(`${schedule.dateConfirm} ${schedule.startTimeConfirm}`);
    return startDateTime.isValid() && startDateTime.isBefore(dayjs());
  }, []);

  // Check if talent is in an active (non-expired) schedule
  const isTalentInActiveSchedule = useCallback(
    (talentId: number | undefined): boolean => {
      if (!talentId || !caseSchedulesData?.data) return false;
      return caseSchedulesData.data.some((schedule) => {
        const scheduleTalentIds = schedule.talentIds || schedule.talents?.map((t) => t.id) || [];
        const hasTalent = scheduleTalentIds.includes(Number(talentId));
        return hasTalent && !isScheduleExpired(schedule);
      });
    },
    [caseSchedulesData, isScheduleExpired],
  );

  const handleDeleteClick = async (caseMatch: TCaseMatches) => {
    setCaseMatchToDelete(caseMatch);

    // If this is the last remaining match in the case, BE will soft-delete the entire case
    // (DEV-292). Show a single dedicated confirm instead of the regular delete confirm.
    const isLastMatchInCase = totalCount === 1;
    if (isLastMatchInCase) {
      const confirmedCaseDelete = await confirmDeleteLastMatchDialog.show();
      if (!confirmedCaseDelete || !caseMatch.id || !onDeleteCaseMatch) {
        setCaseMatchToDelete(null);
        return;
      }
      await onDeleteCaseMatch(caseMatch.id);
      setCaseMatchToDelete(null);
      return;
    }

    const confirmed = await confirmDeleteDialog.show();
    if (!confirmed || !caseMatch.id || !onDeleteCaseMatch) {
      setCaseMatchToDelete(null);
      return;
    }

    await onDeleteCaseMatch(caseMatch.id);
    setCaseMatchToDelete(null);
  };

  const handleConfirmDelete = () => {
    confirmDeleteDialog.confirm();
  };

  const handleCancelDelete = () => {
    confirmDeleteDialog.cancel();
    setCaseMatchToDelete(null);
  };

  // Handle status downgrade with confirmation modals (date clearing + optional fields)
  const handleStatusDowngradeWithConfirm = useCallback(
    async (currentStatus: number, newStatus: number, caseMatch: TCaseMatches | undefined, applyChange: () => void) => {
      // Check if there are any date fields with actual data that will be cleared
      const affectedFields = getAffectedDateFieldsOnDowngrade(currentStatus, newStatus, caseMatch as Record<string, unknown>);

      // Only show date clearing modal if there are fields with data to clear
      if (affectedFields.length > 0) {
        setPendingDowngradeInfo({ currentStatus, newStatus, caseMatch });
        const dateConfirmed = await confirmStatusDowngradeDialog.show();
        setPendingDowngradeInfo(null);

        if (!dateConfirmed) return;
      }

      // Second modal: confirm optional fields (only if there are actually empty fields)
      const emptyFields = getEmptyOptionalFieldLabels(newStatus, caseMatch);
      if (emptyFields.length > 0) {
        setEmptyOptionalFields(emptyFields);
        const optionalConfirmed = await confirmOptionalFieldsDialog.show();
        setEmptyOptionalFields([]);

        if (!optionalConfirmed) return;
      }

      applyChange();
    },
    [confirmStatusDowngradeDialog, confirmOptionalFieldsDialog],
  );

  /**
   * Execute status change - updates statusMap, clears talentStatusMap, and auto-saves if applicable
   * @param caseMatchId - The ID of the case match to update
   * @param statusNumber - The new status value
   * @param previousStatus - Optional previous status for downgrade detection
   */
  const executeStatusChange = useCallback(
    (caseMatchId: number, statusNumber: number, previousStatus?: number) => {
      // Update status map
      setStatusMap((prev) => ({ ...prev, [caseMatchId]: statusNumber }));

      // Clear talent status for this case match
      setTalentStatusMap((prev) => {
        const updated = { ...prev };
        delete updated[caseMatchId];
        return updated;
      });

      // Update cleared fields: reset or track new fields on downgrade (single state update)
      const isDowngrade = previousStatus !== undefined && previousStatus > statusNumber;
      setLocalClearedFields((prev) => {
        const updated = { ...prev };
        delete updated[caseMatchId];

        if (isDowngrade) {
          const fieldsToClear = getDateFieldKeysToClear(previousStatus, statusNumber);
          if (fieldsToClear.length > 0) {
            updated[caseMatchId] = fieldsToClear;
          }
        }
        return updated;
      });

      // Auto-save if no missing fields, terminal status, or downgrade
      const caseMatch = rows.find((row) => row.id === caseMatchId);
      if (caseMatch && onAutoSaveCaseMatchStatus) {
        const isExempt = getExemptSpecifiedSkillCert(caseMatch.talent?.residenceStatus?.residenceStatus);
        const hasMissingFields = checkMissingRequiredFields(caseMatch, statusNumber, isExempt);
        const isTerminalStatus = [ECaseMatchStatus.FAILED, ECaseMatchStatus.OFFER_CANCELLED, ECaseMatchStatus.SELECTION_CANCELLED].includes(
          statusNumber,
        );

        if (!hasMissingFields || isTerminalStatus || isDowngrade) {
          onAutoSaveCaseMatchStatus(caseMatchId, statusNumber, undefined, previousStatus);
        }
      }
    },
    [rows, onAutoSaveCaseMatchStatus, getExemptSpecifiedSkillCert],
  );

  /**
   * Handle status change for a case match
   * - CANDIDATE_WITHDRAWN: Opens talent status selection modal
   * - Downgrade (new < current): Shows confirmation modal before applying
   * - Normal change: Applies immediately
   */
  const handleStatusChange = useCallback(
    (caseMatchId: number, newStatus: string) => {
      if (!canEdit) return;

      const statusNumber = Number(newStatus);
      const caseMatch = rows.find((row) => row.id === caseMatchId);
      const currentStatus = statusMap[caseMatchId] ?? caseMatch?.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;

      if (statusNumber === ECaseMatchStatus.CANDIDATE_WITHDRAWN) {
        setPendingCaseMatchId(caseMatchId);
        setPendingPreviousStatus(currentStatus);
        selectTalentStatusModalVisible.open();
        return;
      }

      // Only treat as downgrade if:
      // - Moving to a lower status number AND
      // - NOT moving FROM a terminal status (recovery is not downgrade)
      const isFromTerminalStatus = CASE_MATCH_TERMINAL_STATUSES.includes(currentStatus as (typeof CASE_MATCH_TERMINAL_STATUSES)[number]);
      const isDowngrade = statusNumber < currentStatus && !isFromTerminalStatus;

      if (isDowngrade) {
        handleStatusDowngradeWithConfirm(currentStatus, statusNumber, caseMatch, () => executeStatusChange(caseMatchId, statusNumber, currentStatus));
        return;
      }

      executeStatusChange(caseMatchId, statusNumber);
    },
    [statusMap, rows, canEdit, selectTalentStatusModalVisible, handleStatusDowngradeWithConfirm, executeStatusChange],
  );

  const handleTalentStatusConfirm = useCallback(
    (talentStatus: number) => {
      if (pendingCaseMatchId !== null) {
        const caseMatchIdToSave = pendingCaseMatchId;
        setStatusMap((prev) => ({
          ...prev,
          [pendingCaseMatchId]: ECaseMatchStatus.CANDIDATE_WITHDRAWN,
        }));
        setTalentStatusMap((prev) => ({
          ...prev,
          [pendingCaseMatchId]: talentStatus,
        }));
        setPendingCaseMatchId(null);
        setPendingPreviousStatus(null);

        // Auto-save for CANDIDATE_WITHDRAWN (terminal status, no validation needed)
        onAutoSaveCaseMatchStatus?.(caseMatchIdToSave, ECaseMatchStatus.CANDIDATE_WITHDRAWN, talentStatus);
      }
      selectTalentStatusModalVisible.close();
    },
    [pendingCaseMatchId, selectTalentStatusModalVisible, onAutoSaveCaseMatchStatus],
  );

  const handleTalentStatusModalClose = useCallback(() => {
    if (pendingCaseMatchId !== null && pendingPreviousStatus !== null) {
      setStatusMap((prev) => ({
        ...prev,
        [pendingCaseMatchId]: pendingPreviousStatus,
      }));
      setPendingCaseMatchId(null);
      setPendingPreviousStatus(null);
    }
    selectTalentStatusModalVisible.close();
  }, [pendingCaseMatchId, pendingPreviousStatus, selectTalentStatusModalVisible]);

  // Apply bulk status changes to local statusMap. Optimistic so UI reflects the change before any API round-trip.
  const applyStatusChanges = useCallback(
    (changes: TBulkStatusChange[]) => {
      setStatusMap((prev) => {
        const next = { ...prev };
        changes.forEach(({ id, status }) => {
          next[id] = status;
        });
        return next;
      });
    },
    [onStatusMapChange],
  );

  const handleBulkSaveApi = useCallback(
    async (changes: TBulkStatusChange[]) => {
      applyStatusChanges(changes);
      // Serialize to avoid racing the shared statusMap/initialStatusMap + N parallel refetches inside onAutoSaveCaseMatchStatus.
      for (const { id, status } of changes) {
        await onAutoSaveCaseMatchStatus?.(id, status);
      }
    },
    [applyStatusChanges, onAutoSaveCaseMatchStatus],
  );

  const handleBulkSaveUiOnly = useCallback(
    (changes: TBulkStatusChange[]) => {
      applyStatusChanges(changes);
    },
    [applyStatusChanges],
  );

  const { data: genderOptions } = useEnum("EGender");

  const caseMatchesWithStatusChange = useMemo(() => {
    const changed: Record<number, boolean> = {};
    rows.forEach((row) => {
      if (row.id) {
        const savedStatus = row.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
        const currentStatus = statusMap[row.id] ?? savedStatus;
        changed[row.id] = currentStatus !== savedStatus;
      }
    });
    return changed;
  }, [rows, statusMap]);

  const caseMatchesWithMissingFields = useMemo(() => {
    const missing: Record<number, boolean> = {};
    rows.forEach((row) => {
      if (row.id) {
        const savedStatus = row.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
        const currentStatus = statusMap[row.id] ?? savedStatus;
        const isExempt = getExemptSpecifiedSkillCert(row.talent?.residenceStatus?.residenceStatus);
        missing[row.id] = checkMissingRequiredFields(row, currentStatus, isExempt);
      }
    });
    return missing;
  }, [rows, statusMap, getExemptSpecifiedSkillCert]);

  // Check if talent's 区分 mismatches job's 区分
  const classificationMismatchMap = useMemo(() => {
    const mismatch: Record<number, boolean> = {};
    rows.forEach((row) => {
      if (row.id) {
        mismatch[row.id] = hasClassificationMismatch(row.talent?.specificBusinessClassificationIds, jobClassificationIds);
      }
    });
    return mismatch;
  }, [rows, jobClassificationIds]);

  // Check talent's 在留期限 status (expired / expiring / null)
  const residenceExpiryMap = useMemo(() => {
    const result: Record<number, TExpiryStatus> = {};
    rows.forEach((row) => {
      if (row.id) {
        result[row.id] = getExpiryStatus(row.talent?.periodOfStay);
      }
    });

    return result;
  }, [rows]);

  // Check talent's パスポート期限 status (expired / expiring / null)
  const passportExpiryMap = useMemo(() => {
    const result: Record<number, TExpiryStatus> = {};
    rows.forEach((row) => {
      if (row.id) {
        result[row.id] = getExpiryStatus(row.talent?.passportExpiryDate);
      }
    });

    return result;
  }, [rows]);

  // Terminal statuses (red) for checking - use centralized constant
  const terminalStatuses = CASE_MATCH_TERMINAL_STATUSES;

  useEffect(() => {
    if (onCaseMatchMissingFieldsUpdate) {
      const updates: Record<number, boolean> = {};
      rows.forEach((row) => {
        if (row.id) {
          const savedStatus = row.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
          const currentStatus = statusMap[row.id] ?? savedStatus;
          const hasMissingFields = caseMatchesWithMissingFields[row.id] ?? false;
          const hasStatusChanged = caseMatchesWithStatusChange[row.id] ?? false;
          const isTerminalStatus = terminalStatuses.includes(currentStatus);

          const shouldBlockSubmit = hasMissingFields && !isTerminalStatus && (hasStatusChanged || savedStatus !== ECaseMatchStatus.BEFORE_PROPOSAL);
          updates[row.id] = shouldBlockSubmit;
        }
      });
      onCaseMatchMissingFieldsUpdate(updates);
    }
  }, [rows, statusMap, caseMatchesWithStatusChange, caseMatchesWithMissingFields, terminalStatuses, onCaseMatchMissingFieldsUpdate]);

  // Render sort header: LocalTableSorting for shared view (no URL nav), TableSorting for internal
  const renderSortHeader = (name: string, label: string) =>
    isSharedView && onSort ? (
      <LocalTableSorting name={name} currentSort={currentSort} onSort={onSort}>
        {label}
      </LocalTableSorting>
    ) : (
      <TableSorting name={name}>{label}</TableSorting>
    );

  const hasExportableMatch = useMemo(
    () =>
      rows.some((row) => {
        const currentStatus = statusMap[row.id] ?? row.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
        return (
          currentStatus === ECaseMatchStatus.OFFER ||
          currentStatus === ECaseMatchStatus.OFFER_ACCEPTED ||
          currentStatus === ECaseMatchStatus.ALREADY_HIRED
        );
      }),
    [rows, statusMap],
  );

  const columns = useMemo<MRT_ColumnDef<TCaseMatches>[]>(
    () => [
      {
        id: "talentNumber",
        header: "人材管理番号",
        Header: renderSortHeader("talent.talentNumber", "人材管理番号"),
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">
                {row.original?.talent?.talentNumber ? truncateWithEllipsis(row.original.talent.talentNumber, MAX_SHOW_TEXT_LENGTH) : EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
        accessorKey: "talent.talentNumber",
      },
      {
        id: "fullName",
        header: "名前",
        Header: renderSortHeader("talent.fullName", "名前"),
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center" gap={1}>
              <TalentAvatarCell
                filePath={row.original.talent?.avatarThumbnailPath}
                name={row.original.talent?.fullName}
                shareUrl={isSharedView ? shareUrl : undefined}
              />
              <Typography variant="body2">
                {row.original?.talent?.fullName ? truncateWithEllipsis(row.original.talent.fullName, MAX_SHOW_TEXT_LENGTH) : EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
      },
      {
        id: "nickName",
        header: "呼び名",
        Header: renderSortHeader("talent.nickName", "呼び名"),
        Cell: ({ row }) =>
          row.original?.talent?.nickName ? truncateWithEllipsis(row.original.talent.nickName, MAX_SHOW_TEXT_LENGTH) : EMPTY_STR.TEXT,
      },
      {
        id: "tag",
        header: "タグ",
        Header: renderSortHeader("caseMatch.tag", "タグ"),
        Cell: ({ row }) => <CaseMatchTagList tagIds={row.original?.tagIds ?? []} maxVisible={3} />,
      },
      {
        id: "interviewDate",
        header: "面接日",
        Header: renderSortHeader("caseMatches.interviewDate", "面接日"),
        Cell: ({ row }) => {
          const date = row.original?.interviewDate;
          return date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.TEXT;
        },
      },
      {
        id: "status",
        header: "ステータス",
        Cell: ({ row }) => {
          const caseMatchId = row.original.id;
          const currentStatus = statusMap[caseMatchId] ?? row.original.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;

          return (
            <SelectColor
              nonControl
              options={CaseMatchStatusOptions}
              value={currentStatus}
              onChange={(value) => handleStatusChange(caseMatchId, value)}
              size="md"
              clearable={false}
              optionColors={CASE_MATCH_STATUS_COLOR}
              disabled={!canEdit && !isSharedView}
              readonly={isSharedView}
            />
          );
        },
      },
      {
        id: "nationalityName",
        accessorKey: "talent.nationality.nationalityName",
        header: "国籍",
        Header: renderSortHeader("nationality.nationalityName", "国籍"),
        Cell: ({ row }) => {
          return (
            <Stack direction="row" alignItems="center">
              <Typography variant="body2">
                {row.original?.talent?.nationality?.nationalityName
                  ? truncateWithEllipsis(row.original.talent.nationality.nationalityName, MAX_SHOW_TEXT_LENGTH)
                  : EMPTY_STR.TEXT}
              </Typography>
            </Stack>
          );
        },
      },
      {
        id: "gender",
        accessorKey: "talent.gender",
        header: "性別",
        Cell: ({ row }) => findLabelByValue(genderOptions, row.original?.talent?.gender),
      },
      {
        id: "age",
        header: "年齢",
        Cell: ({ row }) => {
          const birthday = row.original?.talent?.birthday;
          if (!birthday) return EMPTY_STR.TEXT;
          const today = new Date();
          const birthDate = new Date(String(birthday));

          let age = today.getFullYear() - birthDate.getFullYear();

          const hasHadBirthdayThisYear =
            today.getMonth() > birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

          if (!hasHadBirthdayThisYear) {
            age--;
          }

          return age;
        },
      },
      {
        id: "residenceStatusId",
        header: "在留資格",
        Cell: ({ row }) => row.original?.talent?.residenceStatus?.residenceStatus ?? EMPTY_STR.TEXT,
        Header: renderSortHeader("residenceStatus.residenceStatus", "在留資格"),
      },
      {
        id: "periodOfStay",
        header: "在留期限",
        Header: renderSortHeader("talent.periodOfStay", "在留期限"),
        Cell: ({ row }) => {
          const date = row.original?.talent?.periodOfStay;
          return date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.TEXT;
        },
      },
      {
        id: "languageCertificates",
        header: "日本語能力",
        Cell: ({ row }) => row.original?.talent?.languageCertificates ?? EMPTY_STR.TEXT,
        Header: renderSortHeader("languageTestLevel.level", "日本語能力"),
      },
      {
        id: "personInChargeId",
        header: "担当者",
        Cell: ({ row }) =>
          row.original?.talent?.personInCharge?.fullName
            ? truncateWithEllipsis(row.original.talent.personInCharge.fullName, MAX_SHOW_TEXT_LENGTH)
            : EMPTY_STR.TEXT,
        Header: renderSortHeader("personInCharge.fullName", "担当者"),
      },
      {
        id: "actions",
        header: "",
        Header: (
          <BulkActionsHeader
            caseId={caseId}
            totalCount={totalCount}
            caseName={caseName}
            shareUrl={shareUrl}
            isSharedView={isSharedView}
            onBulkChangeTalentStatus={bulkChangeTalentStatusModalVisible.open}
            filterParams={filterParams}
            canExportDocx={hasExportableMatch}
          />
        ),
        Cell: ({ row }) => {
          const caseMatchId = row.original.id;
          const savedStatus = row.original.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
          const currentStatus = statusMap[caseMatchId] ?? savedStatus;
          const hasMissingFields = caseMatchesWithMissingFields[caseMatchId] ?? false;
          const hasStatusChanged = caseMatchesWithStatusChange[caseMatchId] ?? false;

          // Terminal statuses (red) don't show missing fields warning
          const terminalStatuses = [
            ECaseMatchStatus.FAILED,
            ECaseMatchStatus.CANDIDATE_WITHDRAWN,
            ECaseMatchStatus.OFFER_CANCELLED,
            ECaseMatchStatus.SELECTION_CANCELLED,
          ];
          const isTerminalStatus = terminalStatuses.includes(currentStatus);
          const shouldShowMissingFields =
            hasMissingFields && !isTerminalStatus && (hasStatusChanged || savedStatus !== ECaseMatchStatus.BEFORE_PROPOSAL);

          const canDelete = savedStatus === ECaseMatchStatus.BEFORE_PROPOSAL || savedStatus === ECaseMatchStatus.PROPOSING;
          const canExportDocx =
            currentStatus === ECaseMatchStatus.OFFER ||
            currentStatus === ECaseMatchStatus.OFFER_ACCEPTED ||
            currentStatus === ECaseMatchStatus.ALREADY_HIRED;

          return (
            <ActionMenuCell
              row={row.original}
              caseId={caseId}
              onViewTalent={handleViewTalent}
              onViewCaseMatch={handleViewCaseMatch}
              onDelete={handleDeleteClick}
              hasMissingFields={shouldShowMissingFields}
              canEdit={canEdit}
              canDelete={canDelete}
              canExportDocx={canExportDocx}
              isSharedView={isSharedView}
              shareUrl={shareUrl}
            />
          );
        },
        size: 50,
        enablePinning: true,
      },
    ],
    [
      genderOptions,
      handleViewTalent,
      handleViewCaseMatch,
      statusMap,
      handleStatusChange,
      caseMatchesWithMissingFields,
      caseMatchesWithStatusChange,
      canEdit,
      isSharedView,
      currentSort,
      onSort,
      filterParams,
      hasExportableMatch,
    ],
  );
  const isLoading = false;
  const tableRef = useRef<HTMLDivElement>(null);
  const { table } = useTable({
    columns,
    rows,
    pinningColumns: ["actions"],
    muiTableContainerProps: {
      sx: { tableLayout: "fixed" },
    },
    muiTableBodyRowProps: ({ row }) => {
      const caseMatchId = row.original.id;
      const savedStatus = row.original.status ?? ECaseMatchStatus.BEFORE_PROPOSAL;
      const currentStatus = statusMap[caseMatchId] ?? savedStatus;
      const hasMissingFields = caseMatchesWithMissingFields[caseMatchId] ?? false;
      const hasStatusChanged = caseMatchesWithStatusChange[caseMatchId] ?? false;
      const hasClassificationMismatch = classificationMismatchMap[caseMatchId] ?? false;
      const residenceExpiry = residenceExpiryMap[caseMatchId] ?? null;
      const passportExpiry = passportExpiryMap[caseMatchId] ?? null;

      // Terminal statuses (red) don't show required field warning
      const terminalStatuses = [
        ECaseMatchStatus.FAILED,
        ECaseMatchStatus.CANDIDATE_WITHDRAWN,
        ECaseMatchStatus.OFFER_CANCELLED,
        ECaseMatchStatus.SELECTION_CANCELLED,
      ];
      const isTerminalStatus = terminalStatuses.includes(currentStatus);
      const shouldShowWarning = isSharedView
        ? false
        : hasMissingFields && !isTerminalStatus && (hasStatusChanged || savedStatus !== ECaseMatchStatus.BEFORE_PROPOSAL);
      const shouldShowClassificationWarning = isSharedView ? false : hasClassificationMismatch;
      const effectivePassportExpiry = isSharedView ? null : passportExpiry;
      const warningCount =
        (shouldShowWarning ? 1 : 0) + (shouldShowClassificationWarning ? 1 : 0) + (residenceExpiry ? 1 : 0) + (effectivePassportExpiry ? 1 : 0);
      const rowHeight = warningCount > 0 ? 42 + warningCount * 24 : undefined;

      return {
        sx: {
          position: "relative",
          ...(rowHeight && { height: `${rowHeight}px` }),
        },
        "data-case-match-id": caseMatchId,
        "data-show-warning": shouldShowWarning ? "true" : "false",
        "data-show-classification-warning": shouldShowClassificationWarning ? "true" : "false",
        "data-residence-expiry": residenceExpiry || "",
        "data-passport-expiry": effectivePassportExpiry || "",
      };
    },
    defaultColumn: {
      minSize: 50,
      maxSize: Number.MAX_VALUE,
      size: 1,
    },
    state: {},
  });

  // Apply display column settings (order + visibility) via MRT API
  useEffect(() => {
    if (!displayColumnSettings) return;

    const orderedIds = displayColumnSettings.filter((s) => s.visible).map((s) => s.id);
    table.setColumnOrder([...orderedIds, "actions"]);

    const visibilityMap: Record<string, boolean> = {};
    displayColumnSettings.forEach((s) => {
      visibilityMap[s.id] = s.visible;
    });
    visibilityMap["actions"] = true;
    table.setColumnVisibility(visibilityMap);
  }, [displayColumnSettings, table]);

  // Inject warning text into rows (skip for shared view)
  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const tableContainer = tableRef.current;
      if (!tableContainer) return;

      const allRows = tableContainer.querySelectorAll<HTMLTableRowElement>("tbody tr[data-case-match-id]");

      allRows.forEach((row) => {
        const showWarning = isSharedView ? false : row.getAttribute("data-show-warning") === "true";
        const showClassificationWarning = isSharedView ? false : row.getAttribute("data-show-classification-warning") === "true";
        const residenceExpiry = row.getAttribute("data-residence-expiry") || "";
        const passportExpiry = row.getAttribute("data-passport-expiry") || "";
        const hasAnyWarning = showWarning || showClassificationWarning || !!residenceExpiry || !!passportExpiry;

        // Remove existing warnings
        row.querySelector(".case-match-warning")?.remove();
        row.querySelector(".case-match-classification-warning")?.remove();
        row.querySelector(".case-match-residence-expiry-warning")?.remove();
        row.querySelector(".case-match-passport-expiry-warning")?.remove();
        let currentTop = 42;

        if (showWarning) {
          const warningElement = document.createElement("div");
          warningElement.className = "case-match-warning";
          warningElement.style.position = "absolute";
          warningElement.style.left = "15px";
          warningElement.style.top = `${currentTop}px`;
          warningElement.style.color = state.warning_1;
          warningElement.style.fontSize = "12px";
          warningElement.style.lineHeight = "16px";
          warningElement.style.fontWeight = "400";
          warningElement.textContent = WARNING_CHANGE_STATUS_MESSAGE;
          row.appendChild(warningElement);
          currentTop += 24;
        }

        if (residenceExpiry) {
          const residenceWarning = document.createElement("div");
          residenceWarning.className = "case-match-residence-expiry-warning";
          residenceWarning.style.position = "absolute";
          residenceWarning.style.left = "15px";
          residenceWarning.style.top = `${currentTop}px`;
          residenceWarning.style.color = state.warning_1;
          residenceWarning.style.fontSize = "12px";
          residenceWarning.style.lineHeight = "16px";
          residenceWarning.style.fontWeight = "400";
          residenceWarning.textContent = residenceExpiry === EXPIRY_STATUS.EXPIRED ? RESIDENCE_EXPIRED_MESSAGE : RESIDENCE_EXPIRING_SOON_MESSAGE;
          row.appendChild(residenceWarning);
          currentTop += 24;
        }

        if (passportExpiry) {
          const passportWarning = document.createElement("div");
          passportWarning.className = "case-match-passport-expiry-warning";
          passportWarning.style.position = "absolute";
          passportWarning.style.left = "15px";
          passportWarning.style.top = `${currentTop}px`;
          passportWarning.style.color = state.warning_1;
          passportWarning.style.fontSize = "12px";
          passportWarning.style.lineHeight = "16px";
          passportWarning.style.fontWeight = "400";
          passportWarning.textContent = passportExpiry === EXPIRY_STATUS.EXPIRED ? PASSPORT_EXPIRED_MESSAGE : PASSPORT_EXPIRING_SOON_MESSAGE;
          row.appendChild(passportWarning);
          currentTop += 24;
        }

        if (showClassificationWarning) {
          const classificationWarning = document.createElement("div");
          classificationWarning.className = "case-match-classification-warning";
          classificationWarning.style.position = "absolute";
          classificationWarning.style.left = "15px";
          classificationWarning.style.top = `${currentTop}px`;
          classificationWarning.style.color = state.warning_1;
          classificationWarning.style.fontSize = "12px";
          classificationWarning.style.lineHeight = "16px";
          classificationWarning.style.fontWeight = "400";
          classificationWarning.textContent = CLASSIFICATION_MISMATCH_MESSAGE;
          row.appendChild(classificationWarning);
        }

        // Push content into top portion of expanded rows using padding-bottom.
        // Keep vertical-align: middle (default) so all cell content types stay aligned.
        const warningCount = (showWarning ? 1 : 0) + (showClassificationWarning ? 1 : 0) + (!!residenceExpiry ? 1 : 0) + (!!passportExpiry ? 1 : 0);
        const cells = row.querySelectorAll<HTMLTableCellElement>("td");
        cells.forEach((cell) => {
          // Clean up old warning wrapper spans from previous approach
          const oldWrapper = cell.querySelector("span[data-warning-wrapper]");
          if (oldWrapper) {
            const text = oldWrapper.textContent || "";
            oldWrapper.remove();
            if (text) cell.textContent = text;
          }
          if (hasAnyWarning) {
            cell.style.setProperty("padding-bottom", `${warningCount * 24}px`, "important");
          } else {
            cell.style.removeProperty("padding-bottom");
          }
        });
      });
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    rows,
    caseMatchesWithMissingFields,
    statusMap,
    isSharedView,
    classificationMismatchMap,
    residenceExpiryMap,
    passportExpiryMap,
    displayColumnSettings,
  ]);

  // Confirm-modal messages — extracted from JSX so the render tree stays scannable.
  const deleteConfirmMessage = useMemo(() => {
    if (!caseMatchToDelete) return "";
    return isTalentInActiveSchedule(caseMatchToDelete.talent?.id)
      ? "面接スケジュールに追加されていますが、削除してもよろしいでしょうか？"
      : "選択したユーザーを削除してもよろしいですか？";
  }, [caseMatchToDelete, isTalentInActiveSchedule]);

  const deleteLastMatchMessage = (
    <>
      <span style={{ color: state.warning_1 }}>
        選択した外国人を削除します。
        <br />
        また、この操作を行うと案件も削除されます。
      </span>
      <br />
      <br />
      削除後は求人一覧に遷移します。
    </>
  );

  const statusDowngradeMessage = useMemo(() => {
    if (!pendingDowngradeInfo) {
      return "以下の日付情報はクリアされますがよろしいですか？";
    }
    const fields = getAffectedDateFieldsOnDowngrade(
      pendingDowngradeInfo.currentStatus,
      pendingDowngradeInfo.newStatus,
      pendingDowngradeInfo.caseMatch as Record<string, unknown>,
    );
    return (
      <>
        以下の日付情報はクリアされますがよろしいですか？
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          {fields.map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      </>
    );
  }, [pendingDowngradeInfo]);

  const optionalFieldsMessage = useMemo(
    () => (
      <>
        以下の項目が未入力ですが、このまま保存してもよろしいですか？
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          {emptyOptionalFields.map((field) => (
            <li key={field}>{field}</li>
          ))}
        </ul>
      </>
    ),
    [emptyOptionalFields],
  );

  return (
    <>
      <div ref={tableRef}>
        <Table table={table} isLoading={isLoading} maxHeight={440} headRowHeight={40} bodyRowHeight={42} />
      </div>
      {!isSharedView && (
        <>
          <TalentInfoModal isOpen={talentInfoModalVisible.visible} talentId={selectedTalentId} onClose={handleCloseTalentModal} />
          <CaseMatchInfoModal
            key={selectedCaseMatch?.id}
            isOpen={CaseMatchInfoModalVisible.visible}
            caseMatch={selectedCaseMatch}
            currentStatus={selectedCaseMatch?.id ? statusMap[selectedCaseMatch.id] : undefined}
            onClose={handleCloseCaseMatchModal}
            onSuccess={handleCaseMatchSaveSuccess}
            canEdit={canEdit}
            exemptSpecifiedSkillCert={getExemptSpecifiedSkillCert(selectedCaseMatch?.talent?.residenceStatus?.residenceStatus)}
          />
          <CaseMatchDetailModal
            key={`detail-${selectedCaseMatch?.id}`}
            isOpen={CaseMatchDetailModalVisible.visible}
            caseMatch={selectedCaseMatch}
            onClose={handleCloseCaseMatchDetailModal}
            onEdit={handleEditFromDetail}
            canEdit={canEdit}
          />
        </>
      )}
      {isSharedView && shareUrl && (
        <>
          <SharedTalentInfoModal
            isOpen={talentInfoModalVisible.visible}
            talentId={selectedTalentId}
            onClose={handleCloseTalentModal}
            shareUrl={shareUrl}
          />
          <SharedCaseMatchDetailModal
            key={`shared-detail-${selectedCaseMatch?.id}`}
            isOpen={CaseMatchDetailModalVisible.visible}
            caseMatch={selectedCaseMatch}
            onClose={handleCloseCaseMatchDetailModal}
            shareUrl={shareUrl}
            casePersonInChargeName={(selectedCaseMatch as TCaseMatches & { caseMatchUser?: { fullName?: string } })?.caseMatchUser?.fullName}
            talentPersonInChargeName={
              (selectedCaseMatch?.talent as TCaseMatches["talent"] & { personInCharge?: { fullName?: string } })?.personInCharge?.fullName
            }
          />
        </>
      )}
      <ConfirmDeleteModal open={confirmDeleteDialog.open} onClose={handleCancelDelete} onOk={handleConfirmDelete} message={deleteConfirmMessage} />
      <ConfirmDeleteModal
        open={confirmDeleteLastMatchDialog.open}
        onClose={confirmDeleteLastMatchDialog.cancel}
        onOk={confirmDeleteLastMatchDialog.confirm}
        message={deleteLastMatchMessage}
      />
      <ConfirmModal
        open={confirmStatusDowngradeDialog.open}
        onClose={confirmStatusDowngradeDialog.cancel}
        onOk={confirmStatusDowngradeDialog.confirm}
        message={statusDowngradeMessage}
      />
      <ConfirmModal
        open={confirmOptionalFieldsDialog.open}
        onClose={confirmOptionalFieldsDialog.cancel}
        onOk={confirmOptionalFieldsDialog.confirm}
        message={optionalFieldsMessage}
      />
      <SelectTalentStatusModal
        open={selectTalentStatusModalVisible.visible}
        onClose={handleTalentStatusModalClose}
        onConfirm={handleTalentStatusConfirm}
      />
      {!isSharedView && (
        <BulkChangeTalentStatusModal
          open={bulkChangeTalentStatusModalVisible.visible}
          onClose={bulkChangeTalentStatusModalVisible.close}
          rows={rows}
          onSaveApi={handleBulkSaveApi}
          onSaveUiOnly={handleBulkSaveUiOnly}
          jobResidenceStatusIds={jobResidenceStatusIds}
        />
      )}
    </>
  );
};
